import type { MouseEvent, ReactNode } from "react";

import {
  Header as BaseHeader,
  IconButton,
  NavLink,
  navLinkClassName,
  useThemeByPath,
} from "@partners/ui";
import { Button } from "@pepperstone/pepper-ui-kit";
import React, { useState } from "react";

import { SideNavBar } from "@/components/SideNavBar";
import { useGlobalAnalytics } from "@/hooks/analytics";
import { useIsMobile } from "@/hooks/useIsMobile";
import { isServedToChina } from "@/utils/domainResolver";
import { openInSameTab } from "@/utils/openInSameTab";

import { LAUNCH_PERSONAL_TRADING_URL } from ".";
import { HeaderLogo } from "../HeaderLogo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import {
  NavOverflowIcon,
  NavOverflowMenu,
  navOverflowTriggerClassName,
} from "./NavOverflowMenu";
import { useNavOverflow } from "./useNavOverflow";

export type NavLinkData = {
  name:
    | "Overview"
    | "Marketing"
    | "Leads"
    | "Clients"
    | "Earnings"
    | "Performance"
    | "Export report"
    | "IB network";
  segmentEvent: string;
  to: string;
  /**
   * Query params for the link, e.g. `{ tab: "shared-rebate-program" }`.
   * Kept separate from `to` (never embed a raw `?query` in `to` itself) —
   * `isNavLinkSelected` below compares `to` against `location.pathname`
   * (no query string), and `<Link>`'s own route matching expects `to` to be
   * a bare path with params passed through this prop, not string-glued on.
   */
  search?: Record<string, string>;
  label: string;
  icon?: ReactNode;
};

type AuthenticatedHeaderProps = {
  navLinks: NavLinkData[];
  selectedNavLink: string;
  launchPersonalTradingLinkLabel: string;
  isIdentityOnlyUser: boolean;
  profileMenuComponent?: ReactNode;
};

export const AuthenticatedBaseHeader = ({
  navLinks,
  selectedNavLink,
  launchPersonalTradingLinkLabel,
  isIdentityOnlyUser = false,
  profileMenuComponent,
}: AuthenticatedHeaderProps) => {
  const { themeMode } = useThemeByPath();
  const [isMobileSideNavbarOpen, setIsMobileSideNavbarOpen] = useState(false);
  const isMobile = useIsMobile();
  const { setContainer, setRuler, isMeasuring, visibleCount } = useNavOverflow(
    navLinks.length,
    navLinks.map(({ label }) => label).join("|"),
  );
  const {
    trackNavigationHeaderEvent,
    trackNavigationMobileMenuEvent,
    trackLaunchPersonalTradingHeaderEvent,
  } = useGlobalAnalytics();

  const isNavLinkSelected = (linkPath: string) =>
    linkPath === "/"
      ? selectedNavLink === "/"
      : selectedNavLink.startsWith(linkPath);

  const mobileNavLinkNodes = navLinks.map(
    ({ segmentEvent, to, search, label, icon }, idx) => (
      <NavLink
        key={`${to}-${idx}`}
        to={to}
        search={search}
        isSelected={isNavLinkSelected(to)}
        onClick={(event: MouseEvent<HTMLAnchorElement>) => {
          if (segmentEvent) trackNavigationMobileMenuEvent(event, segmentEvent);
          setIsMobileSideNavbarOpen(false);
        }}
      >
        <div className="flex items-center gap-x-4">
          {icon}
          {label}
        </div>
      </NavLink>
    ),
  );

  const navLinkNodes = navLinks.map(
    ({ segmentEvent, to, search, label }, idx) => (
      <NavLink
        key={`${to}-${idx}`}
        to={to}
        search={search}
        isSelected={isNavLinkSelected(to)}
        onClick={(event: MouseEvent<HTMLAnchorElement>) => {
          if (segmentEvent) trackNavigationHeaderEvent(event, segmentEvent);
        }}
      >
        {label}
      </NavLink>
    ),
  );

  const visibleNavLinks = navLinks.slice(0, visibleCount);
  const overflowNavLinks = navLinks.slice(visibleCount);

  // Group Leads and Clients together if they are consecutive
  const structuredNavItems = visibleNavLinks.reduce<React.ReactNode[]>(
    (acc, link, index) => {
      const nextLink = visibleNavLinks[index + 1];

      if (link.name === "Leads" && nextLink?.name === "Clients") {
        acc.push(
          <div
            key="leads-clients-nav"
            id="leads-clients-nav"
            className="flex items-center gap-2"
          >
            <li key={link.to}>{navLinkNodes[index]}</li>
            <li key={nextLink.to}>{navLinkNodes[index + 1]}</li>
          </div>,
        );
        return acc;
      }

      if (
        link.name === "Clients" &&
        visibleNavLinks[index - 1]?.name === "Leads"
      ) {
        return acc;
      }

      acc.push(<li key={link.to}>{navLinkNodes[index]}</li>);
      return acc;
    },
    [],
  );

  const headerCenterElement = !isMobile ? (
    <div className="min-w-0 flex-grow px-6">
      {navLinkNodes.length > 0 && (
        <nav data-testid="navbar">
          <div ref={setContainer} className="relative flex items-center gap-2">
            <ul className="flex items-center gap-2 overflow-hidden whitespace-nowrap">
              {structuredNavItems}
            </ul>
            {overflowNavLinks.length > 0 && (
              <NavOverflowMenu
                items={overflowNavLinks}
                isNavLinkSelected={isNavLinkSelected}
                onNavigate={trackNavigationHeaderEvent}
              />
            )}
            {isMeasuring && (
              <div
                aria-hidden
                className="pointer-events-none invisible absolute inset-0 overflow-hidden"
              >
                <div
                  ref={setRuler}
                  className="flex w-max items-center gap-2 whitespace-nowrap"
                >
                  {navLinks.map(({ to, label }) => (
                    <div key={to}>
                      <div className={navLinkClassName}>{label}</div>
                    </div>
                  ))}
                  <div className={navOverflowTriggerClassName}>
                    <NavOverflowIcon />
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>
      )}
    </div>
  ) : undefined;

  const handlePersonalTradingOnClick = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    if (isServedToChina()) {
      openInSameTab(
        LAUNCH_PERSONAL_TRADING_URL,
        trackLaunchPersonalTradingHeaderEvent,
      )(event);
    }
  };

  return (
    <>
      {isMobile && (
        <SideNavBar
          navLinksNodes={mobileNavLinkNodes}
          isOpen={isMobileSideNavbarOpen}
          onClose={() => setIsMobileSideNavbarOpen(false)}
        />
      )}
      <BaseHeader
        leftElement={
          <div className="flex shrink-0 items-center gap-2">
            {isMobile && themeMode === "blue" && (
              <div id="mobile-menu-button">
                <IconButton
                  icon="menu"
                  size="medium"
                  color="white"
                  onClick={() => setIsMobileSideNavbarOpen(true)}
                />
              </div>
            )}
            <HeaderLogo darkLogo={themeMode !== "blue"} />
          </div>
        }
        centerElement={headerCenterElement}
        rightElement={
          <div className="flex shrink-0 items-center gap-2">
            {(!isMobile || themeMode !== "blue") && !isIdentityOnlyUser && (
              <Button
                as="a"
                variant="ps-btn-secondary"
                theme="dark"
                type="button"
                href={LAUNCH_PERSONAL_TRADING_URL}
                onClick={handlePersonalTradingOnClick}
              >
                {launchPersonalTradingLinkLabel}
              </Button>
            )}
            {profileMenuComponent}
            {(!isMobile || themeMode !== "blue") && <LanguageSwitcher />}
          </div>
        }
      />
    </>
  );
};
